import { getCoreUtils, kCore, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { DropboxReadRules } from "./DropboxReadRules";
import { AppUpdateEvery } from "./AppUpdateEvery";

// AppSearch - the rules of the main screen's search: folding, matching, the snippet, the hit list.
//
// ⚠ RULES ONLY - NO `window`, NO `XMLHttpRequest`, NO `document`. The Bridge suite may import this
// file to pin `search.json` against the service that writes it, and a transport in here would pass
// under tsx and fail under tsc (the `AppConnect`/`AppConnectWatch` trap). The DOM side is
// `AppSearchPanel`; the parse is `AppSearchIndex`.
//
// ⛔ THE SNIPPET IS BUILT FROM THE ORIGINAL TEXT, ESCAPED SEGMENT BY SEGMENT, AND ONLY THEN MARKED.
// Matching happens in the FOLDED text (no accents, lower case, ß as ss), and folding can change the
// length - so a folded offset is mapped back through `FoldMap` to the original characters. Running a
// highlight regex over already-built HTML would match inside `&amp;` and corrupt the markup, and
// marking the folded text would show the owner words they never wrote ("ostgota" for "Östgöta").

/** One message of one chat, as `search.json` carries it, plus its folded text (made once, at load). */
export class AppSearchMessage {
    public MessageId: string = "";
    /** UTC minute, `YYYY-MM-DDTHH:MM` - 16 characters. */
    public Minute: string = "";
    public FromMe: boolean = false;
    public Text: string = "";
    /** The bubble is on the contact page (its anchor exists), so a link can land on it. */
    public OnPage: boolean = false;
    /** `AppSearch.Fold(Text)`, computed once when the file is read - never per keystroke. */
    public Folded: string = "";
    /** ⭐ TRANSLATION: the message in the owner's language (the tuple's optional 5th element), "" when none. */
    public Translation: string = "";
    /** `AppSearch.Fold(Translation)`, made once at load too. */
    public FoldedTranslation: string = "";
}

/** One chat of `search.json`. `Href` is `index.json`'s `contacts[].href` - the same value. */
export class AppSearchChat {
    public ChatId: string = "";
    public Href: string = "";
    public Title: string = "";
    public PersonKey: string = "";
    /** The service kept only the newest part of a long chat (`perContactChars`). */
    public Clipped: boolean = false;
    public Messages: AppSearchMessage[] = [];
}

/** The whole file, read. `Loaded` false means `Problem` says why - never a screen of blanks. */
export class AppSearchData {
    public Loaded: boolean = false;
    public Problem: string = "";
    public Chats: AppSearchChat[] = [];
}

/** One match, and where it sat in the file (`Order`), so equal minutes keep the file's order. */
export class AppSearchHit {
    public Chat: AppSearchChat = new AppSearchChat();
    public Msg: AppSearchMessage = new AppSearchMessage();
    public Order: number = 0;
    /** ⭐ TRANSLATION: the words matched the translation, not the original - the snippet shows the translation. */
    public ViaTranslation: boolean = false;
}

export class AppSearchResult {
    public NextOffset: number = 0;
    public Total: number = 0;
    public Chats: number = 0;
    /** Newest first, at most `Max`. */
    public Hits: AppSearchHit[] = [];
}

/** A folded string and, per folded code unit, the ORIGINAL range it came from. */
export class AppSearchFilter {
    public Href: string = "";
    public FromMe: string = "";
    public After: string = "";
    public Before: string = "";
    public Offset: number = 0;
}

export class AppFoldMap {
    public Folded: string = "";
    public Starts: number[] = [];
    public Ends: number[] = [];
}

/** A range of the ORIGINAL text, `[S, E)`. */
export class AppRange {
    public S: number = 0;
    public E: number = 0;
}

/** Draws a page of hits for `AppSearch.ResultsWith` (the plain list, or the search actions' groups). */
export interface IAppSearchHitsMarkup {
    Markup(Hits: AppSearchHit[], Words: string[]): string;
}

/** The plain list of hits (`ul.hits`), as `AppSearch.ResultsHtml` draws it. */
export class AppSearchListHits implements IAppSearchHitsMarkup {
    public Markup(Hits: AppSearchHit[], Words: string[]): string {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<ul class=\"hits\">"];
            for (const Hit of Hits) { Out.push(AppSearch.HitHtml(Hit, Words)); }
            Out.push("</ul>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchListHits.Markup");
            return "";
        }
    }
}

export class AppSearch {
    /** The element the hits are painted into - its own element, so the box is never repainted. */
    public static readonly HitsId: string = "wac-msghits";
    /** Two folded characters before message text is searched - one would match half of everything. */
    public static readonly MinChars: number = 2;
    /** How many hits are painted. The count line says when there were more. */
    public static readonly Max: number = 50;
    /** Characters of context either side of the first match. */
    public static readonly Radius: number = 40;
    /**
     * Case- and accent-free: NFD, the combining marks stripped, lower case, ß as ss.
     * So "Östgöta" and "ostgota" are the same word, and "å"/"ä"/"ö" match "a"/"o".
     */
    public static Fold = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return kCore.FoldAccents(nz(Text), true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Fold");
            return "";
        }
    };

    /** The folded words of a query, each once. "" words are dropped. */
    public static Words = (Query: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            for (const One of AppSearch.Fold(Query).trim().split(/\s+/)) {
                if (One.length === 0 || Out.indexOf(One) >= 0) { continue; }
                Out.push(One);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Words");
            return [];
        }
    };

    /** Is the query long enough to search message text (and so to load the file)? */
    public static Ready = (Query: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppSearch.Fold(Query).trim().length >= AppSearch.MinChars;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Ready");
            return false;
        }
    };

    /** ⛔ AND, NOT OR: every word must be in the message. "anna faktura" is a search, not two. */
    public static Matches = (Folded: string, Words: string[]): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Words.length === 0) { return false; }
            for (const One of Words) {
                if (Folded.indexOf(One) < 0) { return false; }
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Matches");
            return false;
        }
    };

    /** Every matching message across every chat - counted in full, the newest `Max` kept. */
    public static Find = (Data: AppSearchData, Words: string[], Max: number,
                          Filter: AppSearchFilter = new AppSearchFilter()): AppSearchResult => {
        if (getCoreUtils().KErrorMode === true) { return new AppSearchResult(); }
        try {
            const Out: AppSearchResult = new AppSearchResult();
            const All: AppSearchHit[] = [];
            for (const Chat of Data.Chats) {
                if (Filter.Href !== "" && Chat.Href !== Filter.Href) { continue; }
                let Any: boolean = false;
                for (const Msg of Chat.Messages) {
                    if (AppSearch.Allowed(Msg, Filter) === false) { continue; }
                    // ⭐ THE ORIGINAL OR ITS TRANSLATION (every word in one of them) - the original is tried first.
                    const InOriginal: boolean = AppSearch.Matches(Msg.Folded, Words);
                    const InTranslation: boolean = InOriginal === false && Msg.FoldedTranslation.length > 0
                        && AppSearch.Matches(Msg.FoldedTranslation, Words) === true;
                    if (InOriginal === false && InTranslation === false) { continue; }
                    const Hit: AppSearchHit = new AppSearchHit();
                    Hit.Chat = Chat;
                    Hit.Msg = Msg;
                    Hit.ViaTranslation = InTranslation;
                    Hit.Order = All.length;
                    All.push(Hit);
                    Any = true;
                }
                if (Any === true) { Out.Chats = Out.Chats + 1; }
            }
            Out.Total = All.length;
            All.sort(AppSearch.Newer);
            const Offset: number = Math.max(0, Math.floor(Filter.Offset));
            Out.Hits = All.slice(Offset, Offset + Max);
            if (Offset + Max < All.length) { Out.NextOffset = Offset + Max; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Find");
            return new AppSearchResult();
        }
    };

    /**
     * Newest minute first; the file's own order breaks a tie.
     * DOCUMENTED EXCEPTION: a sort comparator over two values in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw; `Find` is guarded.
     */
    private static Newer = (A: AppSearchHit, B: AppSearchHit): number => {
        if (A.Msg.Minute > B.Msg.Minute) { return -1; }
        if (A.Msg.Minute < B.Msg.Minute) { return 1; }
        return A.Order - B.Order;
    };

    /** DOCUMENTED EXCEPTION: pure filter predicate on validated search data. */
    private static Allowed = (Msg: AppSearchMessage, Filter: AppSearchFilter): boolean => {
        if (Filter.FromMe === "me" && Msg.FromMe === false) { return false; }
        if (Filter.FromMe === "them" && Msg.FromMe === true) { return false; }
        if (Filter.After !== "" && Msg.Minute.substring(0, 10) < Filter.After) { return false; }
        if (Filter.Before !== "" && Msg.Minute.substring(0, 10) > Filter.Before) { return false; }
        return true;
    };

    /**
     * Fold one code point at a time, remembering where each folded unit came from.
     *
     * ⚠ PER CODE POINT, BECAUSE FOLDING CHANGES LENGTH: "ß" becomes "ss", a decomposed "o" + U+0308
     * becomes "o" + nothing, and a surrogate pair is one character in two units.
     */
    public static FoldMap = (Text: string): AppFoldMap => {
        if (getCoreUtils().KErrorMode === true) { return new AppFoldMap(); }
        try {
            const Out: AppFoldMap = new AppFoldMap();
            const Parts: string[] = [];
            const Src: string = nz(Text);
            let At: number = 0;
            while (At < Src.length) {
                let Len: number = 1;
                const Cp: number | undefined = Src.codePointAt(At);
                if (Cp !== undefined && Cp > 0xffff) { Len = 2; }
                const One: string = AppSearch.Fold(Src.substring(At, At + Len));
                for (let K: number = 0; K < One.length; K = K + 1) {
                    Out.Starts.push(At);
                    Out.Ends.push(At + Len);
                }
                Parts.push(One);
                At = At + Len;
            }
            Out.Folded = Parts.join("");
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.FoldMap");
            return new AppFoldMap();
        }
    };

    /** Every occurrence of every word, as ORIGINAL ranges, sorted and merged. */
    public static Ranges = (Map: AppFoldMap, Words: string[]): AppRange[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Found: AppRange[] = [];
            for (const Word of Words) {
                if (Word.length === 0) { continue; }
                let At: number = Map.Folded.indexOf(Word);
                while (At >= 0) {
                    const One: AppRange = new AppRange();
                    One.S = Map.Starts[At];
                    One.E = Map.Ends[At + Word.length - 1];
                    Found.push(One);
                    At = Map.Folded.indexOf(Word, At + Word.length);
                }
            }
            Found.sort(AppSearch.ByStart);
            return AppSearch.Merged(Found);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Ranges");
            return [];
        }
    };

    /**
     * DOCUMENTED EXCEPTION: a sort comparator over two values in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw; `Ranges` is guarded.
     */
    private static ByStart = (A: AppRange, B: AppRange): number => {
        if (A.S !== B.S) { return A.S - B.S; }
        return A.E - B.E;
    };

    /** Overlapping or touching ranges become one, so a `<mark>` never opens inside another. */
    private static Merged = (Sorted: AppRange[]): AppRange[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppRange[] = [];
            for (const One of Sorted) {
                const Last: AppRange | undefined = Out[Out.length - 1];
                if (Last !== undefined && One.S <= Last.E) {
                    if (One.E > Last.E) { Last.E = One.E; }
                    continue;
                }
                const Copy: AppRange = new AppRange();
                Copy.S = One.S;
                Copy.E = One.E;
                Out.push(Copy);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Merged");
            return [];
        }
    };

    /**
     * About `Radius` characters either side of the first match, as HTML: every text segment
     * escaped, every query word in `<mark>`, "…" where the text was cut.
     */
    public static Snippet = (Text: string, Words: string[], Radius: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Src: string = nz(Text);
            const Marks: AppRange[] = AppSearch.Ranges(AppSearch.FoldMap(Src), Words);
            let From: number = 0;
            let To: number = Math.min(Src.length, Radius * 2);
            if (Marks.length > 0) {
                From = Math.max(0, Marks[0].S - Radius);
                To = Math.min(Src.length, Marks[0].E + Radius);
            }
            From = AppSearch.Whole(Src, From, -1);
            To = AppSearch.Whole(Src, To, 1);
            const Out: string[] = [];
            if (From > 0) { Out.push("…"); }
            let Pos: number = From;
            for (const One of Marks) {
                if (One.E <= From || One.S >= To) { continue; }
                const S: number = Math.max(One.S, From);
                const E: number = Math.min(One.E, To);
                Out.push(kHTMLCore.EscapeHtml(Src.substring(Pos, S)));
                Out.push("<mark>" + kHTMLCore.EscapeHtml(Src.substring(S, E)) + "</mark>");
                Pos = E;
            }
            Out.push(kHTMLCore.EscapeHtml(Src.substring(Pos, To)));
            if (To < Src.length) { Out.push("…"); }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Snippet");
            return "";
        }
    };

    /**
     * A cut point moved off the middle of a surrogate pair (an emoji cut in half renders as "?").
     * DOCUMENTED EXCEPTION: arithmetic on values in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw; `Snippet` is guarded.
     */
    public static Whole = (Src: string, At: number, Way: number): number => {
        if (At <= 0 || At >= Src.length) { return At; }
        const Unit: number = Src.charCodeAt(At);
        if (Unit >= 0xdc00 && Unit <= 0xdfff) { return At + Way; }
        return At;
    };

    /** "37 messages in 5 chats, showing the newest 50" / "No messages match." */
    public static CountLine = (Result: AppSearchResult, Max: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Result.Total === 0) { return "No messages match."; }
            let Line: string = Result.Total.toLocaleString() + " message";
            if (Result.Total !== 1) { Line = Line + "s"; }
            Line = Line + " in " + Result.Chats.toLocaleString() + " chat";
            if (Result.Chats !== 1) { Line = Line + "s"; }
            if (Result.Total > Max) { Line = Line + ", showing the newest " + Max.toLocaleString(); }
            return Line;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.CountLine");
            return "";
        }
    };

    /**
     * The route to the message: its anchor on the contact page when it is there, else the page.
     * ⚠ The anchor is the page's own `m-YYYYMMDDHHMM` - the UTC minute, digits only.
     */
    public static Link = (Href: string, Minute: string, OnPage: boolean, MessageId: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let At: string = "#c/" + encodeURIComponent(nz(Href));
            if (OnPage === true && MessageId !== "") {
                return At + "/" + encodeURIComponent("msg-" + encodeURIComponent(MessageId));
            }
            const Digits: string = nz(Minute).replace(/[^0-9]/g, "");
            if (OnPage === true && Digits.length === 12) {
                At = At + "/" + encodeURIComponent("m-" + Digits);
            }
            return At;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Link");
            return "";
        }
    };

    /** The UTC minute as a local date - "12 Sept 2026" in the reader's own locale; "" if unreadable. */
    public static DateOf = (Minute: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const At: number = Date.parse(nz(Minute) + ":00Z");
            if (isNaN(At) === true) { return ""; }
            return new Date(At).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.DateOf");
            return "";
        }
    };

    /**
     * ⭐ A MESSAGE'S INSTANT AS A PERSON READS IT (audit 2026-10-12): "12 Oct 2026, 14:05" on the reader's own clock.
     * The service's search hits and the "message in its conversation" window showed the wire value itself,
     * "2026-10-12T07:05:00.000Z" - a machine's spelling, in UTC. An unreadable value answers "" (the caller shows nothing).
     */
    public static WhenOf = (Iso: unknown): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (typeof Iso !== "string") { return ""; }
            const At: number = Date.parse(Iso);
            if (isNaN(At) === true) { return ""; }
            return new Date(At).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
                + ", " + new Date(At).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.WhenOf");
            return "";
        }
    };

    /** A to Z, numbers in a name by their value (the house rule for every list a person reads). DOCUMENTED EXCEPTION: a comparer. */
    private static ByTitle = (A: AppSearchChat, B: AppSearchChat): number => {
        return nz(A.Title).localeCompare(nz(B.Title), undefined, { numeric: true });
    };

    /**
     * ⭐ THE MESSAGE SEARCH'S FILTERS, ON THE DESIGN SYSTEM (audit 2026-10-12). They were the browser's own controls in
     * a bare `<fieldset>`: 19-22px tall on a phone, the contact picker pushing the page sideways, and a "Sender" of
     * "Anyone / You / Contact" beside a picker also called "Contact". Each filter is now a labelled field (`sf`: the
     * label above its control, the control drawn by app.css, 44px on a phone); the sender is its own named group of
     * three radios - "Them". ⚠ `data-search-filter` is what `AppSearchPanel.OnFilter` reads; every word is escaped.
     */
    public static FiltersHtml = (Chats: AppSearchChat[], Filter: AppSearchFilter): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Sorted: AppSearchChat[] = Chats.slice().sort(AppSearch.ByTitle);
            const Out: string[] = ["<fieldset class=\"search-filters\"><legend>Filter messages</legend><label class=\"sf\"><span>Contact</span>"
                + "<select data-search-filter=\"Href\"><option value=\"\">All contacts</option>"];
            for (const Chat of Sorted) {
                let Selected: string = "";
                if (Chat.Href === Filter.Href) { Selected = " selected"; }
                Out.push("<option value=\"" + kHTMLCore.EscapeAttr(Chat.Href) + "\"" + Selected + ">" + kHTMLCore.EscapeHtml(Chat.Title) + "</option>");
            }
            Out.push("</select></label><fieldset class=\"sf sfsender\"><legend>Sender</legend><span class=\"sfopts\">");
            for (const Pair of [["", "Anyone"], ["me", "You"], ["them", "Them"]]) {
                let Checked: string = "";
                if (Filter.FromMe === Pair[0]) { Checked = " checked"; }
                Out.push("<label class=\"sfopt\"><input type=\"radio\" name=\"wac-search-sender\" data-search-filter=\"FromMe\" value=\"" + Pair[0]
                    + "\"" + Checked + "><span>" + Pair[1] + "</span></label>");
            }
            Out.push("</span></fieldset><label class=\"sf\"><span>From</span><input type=\"date\" data-search-filter=\"After\" value=\""
                + kHTMLCore.EscapeAttr(Filter.After) + "\"></label><label class=\"sf\"><span>Through</span><input type=\"date\""
                + " data-search-filter=\"Before\" value=\"" + kHTMLCore.EscapeAttr(Filter.Before) + "\"></label></fieldset>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.FiltersHtml");
            return "";
        }
    };

    /**
     * One paging button under the hits, in the button family (`btn secondary sm`). `Attr` is the attribute the panel's
     * click handler reads (`data-search-page="50"`, `data-search-more="1"`); the words are escaped.
     */
    public static Pager = (Attr: string, Value: string, Words: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<button type=\"button\" class=\"btn secondary sm\" " + Attr + "=\"" + kHTMLCore.EscapeAttr(Value) + "\">"
                + kHTMLCore.EscapeHtml(Words) + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Pager");
            return "";
        }
    };

    /** The words a hit matched: its translation when that is what matched, else the original.
     *  DOCUMENTED EXCEPTION: picks one of two fields. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    public static MatchedText = (Hit: AppSearchHit): string => {
        if (Hit.ViaTranslation === true) { return Hit.Msg.Translation; }
        return Hit.Msg.Text;
    };

    /** A hit's date line ("21 Sep  ·  You"), unescaped. */
    public static WhenText = (Hit: AppSearchHit): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const When: string[] = [];
            const Day: string = AppSearch.DateOf(Hit.Msg.Minute);
            if (Day.length > 0) { When.push(Day); }
            if (Hit.Msg.FromMe === true) { When.push("You"); }
            return When.join("  ·  ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.WhenText");
            return "";
        }
    };

    /** The "older - not on the page" marker, or "" for a hit on the page. */
    public static OlderHtml = (Hit: AppSearchHit): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Hit.Msg.OnPage !== true) { return "<span class=\"older\">older - not on the page</span>"; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.OlderHtml");
            return "";
        }
    };

    /** The date line and the marked snippet: `span.when` + `span.snip`. */
    public static SnipHtml = (Hit: AppSearchHit, Words: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<span class=\"when\">" + kHTMLCore.EscapeHtml(AppSearch.WhenText(Hit)) + "</span><span class=\"snip\">"
                + AppSearch.Snippet(AppSearch.MatchedText(Hit), Words, AppSearch.Radius) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.SnipHtml");
            return "";
        }
    };

    /** One hit, as a list item. ⛔ Every piece of text is escaped; the snippet is `Snippet`'s. */
    public static HitHtml = (Hit: AppSearchHit, Words: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<li class=\"hit\"><a href=\"" + kHTMLCore.EscapeAttr(AppSearch.Link(Hit.Chat.Href,
                Hit.Msg.Minute, Hit.Msg.OnPage, Hit.Msg.MessageId)) + "\"><span class=\"who\">"
                + kHTMLCore.EscapeHtml(Hit.Chat.Title) + "</span>" + AppSearch.SnipHtml(Hit, Words)
                + AppSearch.OlderHtml(Hit) + "</a></li>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.HitHtml");
            return "";
        }
    };

    /** The "Messages" section: a heading, the count, the hits. */
    public static ResultsHtml = (Data: AppSearchData, Query: string,
                                 Filter: AppSearchFilter = new AppSearchFilter()): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppSearch.ResultsWith(Data, Query, Filter, new AppSearchListHits());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.ResultsHtml");
            return "";
        }
    };

    /** The "Messages" section with the hits drawn by `Hits`: heading + count, the hits, the page links. */
    public static ResultsWith = (Data: AppSearchData, Query: string, Filter: AppSearchFilter,
                                 Hits: IAppSearchHitsMarkup): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Words: string[] = AppSearch.Words(Query);
            const Result: AppSearchResult = AppSearch.Find(Data, Words, AppSearch.Max, Filter);
            const Out: string[] = [AppSearch.Head(AppSearch.CountLine(Result, AppSearch.Max))];
            if (Result.Hits.length === 0) { return Out.join(""); }
            Out.push(Hits.Markup(Result.Hits, Words));
            const Pages: string[] = [];
            if (Filter.Offset > 0) { Pages.push(AppSearch.Pager("data-search-page", String(Math.max(0, Filter.Offset - AppSearch.Max)), "Previous messages")); }
            if (Result.NextOffset > 0) { Pages.push(AppSearch.Pager("data-search-page", String(Result.NextOffset), "More messages")); }
            if (Pages.length > 0) { Out.push("<p class=\"hitsmore\">" + Pages.join("") + "</p>"); }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.ResultsWith");
            return "";
        }
    };

    /** The section heading plus one sentence in INK - a count, "Searching messages..." or why not. */
    public static Head = (Say: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<h2 class=\"hitshead\">Messages</h2><p class=\"hitsay\">"
                + kHTMLCore.EscapeHtml(nz(Say)) + "</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.Head");
            return "";
        }
    };

    /** Shown while `search.json` is on its way. */
    public static readonly Searching: string = "Searching…";

    /** ⚠ NOT A FAULT: the service builds `search.json` on its own schedule, and a new archive has none. */
    public static readonly NotYetWhy: string = "Message search is being prepared - it appears after"
        + " WACommander's next update (" + AppUpdateEvery.Within + ").";

    /**
     * What a failed read of `search.json` means, in a sentence.
     *
     * ⚠ "NOT THERE" IS THE ORDINARY STATE of an archive the service has not rebuilt since search
     * shipped - Dropbox's `path/not_found` (`DropboxReadRules.Why`'s sentence) or a local 404. Every
     * other failure keeps the source's own sentence, which already says what happened.
     */
    public static LoadWhy = (Why: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Said: string = nz(Why);
            const Missing: string = DropboxReadRules.Why(409, "{\"error_summary\":\"path/not_found/..\"}");
            if (Said === Missing || Said.indexOf("(status 404)") >= 0) { return AppSearch.NotYetWhy; }
            return "Messages could not be searched just now. " + Said;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearch.LoadWhy");
            return "";
        }
    };
}

/**
 * Whether `search.json` is needed, on its way, in hand, or failed - and when it may be tried again.
 *
 * ⛔ ONCE PER PAGE, AND ONE RETRY PER MINUTE AFTER A FAILURE - NEVER A LOOP. Every keystroke calls
 * `WantLoad`; without this a missing file would be asked for on each letter typed.
 */
export class AppSearchState {
    public static readonly RetryMs: number = 60000;
    /** `idle`, `loading`, `ready` or `failed`. */
    public Phase: string = "idle";
    public FailedAt: number = 0;
    public Why: string = "";
    public Data: AppSearchData | null = null;

    public WantLoad = (NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Phase === "idle") { return true; }
            if (this.Phase === "failed" && NowMs - this.FailedAt >= AppSearchState.RetryMs) { return true; }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchState.WantLoad");
            return false;
        }
    };

    public Begin = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Phase = "loading";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchState.Begin");
        }
    };

    public Took = (Data: AppSearchData): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Data = Data;
            this.Phase = "ready";
            this.Why = "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchState.Took");
        }
    };

    public Failed = (Why: string, NowMs: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Phase = "failed";
            this.FailedAt = NowMs;
            this.Why = nz(Why);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchState.Failed");
        }
    };
}
